import { Application } from 'hooks/webapi/useApplication';
import { axiosInstance } from 'utils';
import { Client } from 'hooks/webapi/useClient';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isValidApplication } from 'utils/applicationUtility';
import { LatestLoan } from 'hooks/webapi/useLatestLoan';
import { useApplication, useClient, useLatestLoan } from 'hooks/webapi';
import dayjs from 'dayjs';
import useClientCardSettings from 'hooks/webapi/useClientCardSettings';

type Props = {
  children: ReactNode;
};

type BundleFeeResponse = {
  bundleFee: number;
  lastDateBeforeAutoProcessing?: string;
  parts: number;
  remainingNumberOfDays: number;
  singleFee: number;
};

type Bundle = {
  applyBundleExtension: () => Promise<void>;
  bundleFee: any;
  getBundleFee: () => Promise<void>;
  confirmBundleFee: () => Promise<void>;
  isBundleAvailable: boolean;
  isBundleAccepted: boolean;
  isBundleExtensionAvailable: boolean;
  bundleDays: {
    startDate?: string;
    transferDay: string;
    fullTransferDay: string;
    paymentDay: string;
    paymentDayAdditionalAmount: string;
  };
  isAutoRepaymentMandatory: boolean;
};

export const isBundleLatestloanAccepted = (
  latestLoan: LatestLoan | undefined | null,
) => Boolean(latestLoan?.bundleAccepted);

export const isClientBundle = ({
  latestLoan,
  application,
  client,
}: {
  latestLoan?: LatestLoan | null;
  application?: Application | null;
  client?: Client | null;
}) => {
  if (latestLoan?.bundleOffered) return true;

  if (
    !latestLoan &&
    application &&
    isValidApplication(application, client) &&
    client?.productsEligibleForBundleOffer.includes(
      application.productInfo.number,
    )
  )
    return true;

  return false;
};

const BundleContext = createContext<Bundle>({} as Bundle);

const useBundle = (): Bundle => useContext(BundleContext);

const BundleProvider = ({ children }: Props): JSX.Element => {
  const [bundleFee, setBundleFee] = useState<
    BundleFeeResponse | undefined | null
  >();
  const { application } = useApplication();
  const { latestLoan } = useLatestLoan();
  const { client } = useClient();
  const { clientCardSettings } = useClientCardSettings();

  const isBundleAccepted = isBundleLatestloanAccepted(latestLoan);
  const isBundleAvailable = isClientBundle({
    client,
    application,
    latestLoan,
  });

  const isBundleExtensionAvailable = Boolean(
    latestLoan?.bundleExtensionPossible,
  );

  const getBundleFee = useCallback(async () => {
    try {
      const response = await axiosInstance.get<BundleFeeResponse>(
        '/client/application/bundle/info',
      );
      setBundleFee(response.data);
    } catch {
      setBundleFee(null);
    }
  }, []);

  useEffect(() => {
    if (bundleFee === undefined && client && isBundleAvailable) {
      getBundleFee();
    }
  }, [bundleFee, client, isBundleAvailable]);

  const confirmBundleFee = useCallback(async () => {
    await axiosInstance.put('/client/application/bundle/confirm');
  }, []);

  const applyBundleExtension = useCallback(async () => {
    await axiosInstance.post('/client/loans/latest/bundle-extension');
  }, []);

  const startDate = application?.created;

  const defaultTransferDate = dayjs(startDate).add(7, 'days');
  const transferDate = bundleFee?.lastDateBeforeAutoProcessing
    ? dayjs(bundleFee?.lastDateBeforeAutoProcessing)
    : defaultTransferDate;

  const transferDay = transferDate.format('DD/MM/YYYY');
  const fullTransferDay = transferDate.format('YYYY-MM-DDTHH:mm:ss');

  const additionalPaymentDate =
    latestLoan?.dueDate && dayjs(latestLoan?.dueDate).format('DD/MM/YYYY');
  const repeatedLoanPaymentDate = dayjs(transferDate)
    .add(application?.term || 0, 'days')
    .format('DD/MM/YYYY');

  const bundleDays = {
    startDate,
    transferDay,
    fullTransferDay,
    paymentDayAdditionalAmount: dayjs(
      latestLoan?.lastDateForNewAmountWhenBundleNotAccepted || '',
    ).format('DD/MM/YYYY'),
    paymentDay: additionalPaymentDate || repeatedLoanPaymentDate,
  };

  const isAutoRepaymentMandatory =
    isBundleAvailable &&
    Boolean(clientCardSettings?.autoRepaymentSkipOptionHidden);

  const bundleContextValue = useMemo(
    () => ({
      applyBundleExtension,
      bundleFee,
      confirmBundleFee,
      getBundleFee,
      isBundleAvailable,
      isBundleAccepted,
      isBundleExtensionAvailable,
      bundleDays,
      isAutoRepaymentMandatory,
    }),
    [
      applyBundleExtension,
      bundleFee,
      confirmBundleFee,
      getBundleFee,
      isBundleAvailable,
      isBundleAccepted,
      isBundleExtensionAvailable,
      bundleDays,
      isAutoRepaymentMandatory,
    ],
  );

  return (
    <BundleContext.Provider value={bundleContextValue}>
      {children}
    </BundleContext.Provider>
  );
};

export { useBundle as default, BundleProvider };
